import { useState } from 'react';
import { getApiErrorMessage } from '@shared/utils/apiError';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Pencil, Save, X, CheckCircle, AlertCircle, ChevronDown } from 'lucide-react';
import { clinicApi } from '../services/clinicApi';
import type { ClinicDetailDto, UpdateClinicOnboardingRequest } from '../types/clinic';
import { PreferredMethodOfAppointment, ReportingMethod } from '../types/clinic';
import RequirePermission from '@shared/components/RequirePermission';
import './ClinicInfoSection.css';

interface ClinicOnboardingSectionProps {
  clinic: ClinicDetailDto;
  onUpdate: () => void;
}

interface EditFormState {
  preferredMethodOfAppointment: string;
  reportingMethod: string;
  w9OnFile: boolean;
  dotCertification: boolean;
  numberOfCertifiedDOTProfessionals: number;
  // translationServices is no longer edited here (SLV2-3301): it is the
  // 'Translation Services' capability on the Capabilities card.
}

function buildFormState(clinic: ClinicDetailDto): EditFormState {
  return {
    preferredMethodOfAppointment: clinic.preferredMethodOfAppointment ?? '',
    reportingMethod: clinic.reportingMethod ?? '',
    w9OnFile: clinic.w9OnFile ?? false,
    dotCertification: clinic.dotCertification ?? false,
    numberOfCertifiedDOTProfessionals: clinic.numberOfCertifiedDOTProfessionals ?? 0,
  };
}

export default function ClinicOnboardingSection({ clinic, onUpdate }: Readonly<ClinicOnboardingSectionProps>) {
  const queryClient = useQueryClient();
  const [isEditing, setIsEditing] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [form, setForm] = useState<EditFormState>(() => buildFormState(clinic));
  const [showSuccess, setShowSuccess] = useState(false);
  const [validationError, setValidationError] = useState<string | null>(null);

  // No prop-sync effect needed: view mode reads `clinic` directly, and `form`
  // is rebuilt from the latest clinic in handleEdit/handleCancel (same pattern
  // as ClinicExternalSystemsSection).

  // Onboarding columns are only writable via PUT /clinics/{id}/onboarding
  // (usp_UpdateClinicOnboarding). The previous save went through
  // PUT /clinics/{id}, whose proc ignores onboarding columns entirely — the
  // section showed "Saved" while persisting nothing (SLV2-3154).
  //
  // The proc overwrites every onboarding column, and this section shares the
  // endpoint with the External Systems section (vendor codes) — so carried
  // values are re-fetched at save time rather than taken from the possibly
  // stale `clinic` prop, or saving here could overwrite the other section's
  // just-saved edits.
  const updateMutation = useMutation({
    mutationFn: async (request: Omit<UpdateClinicOnboardingRequest, 'eScreenCode' | 'formFoxCode' | 'questCode' | 'electronicChainOfCustody'>) => {
      const fresh = await clinicApi.getClinicById(clinic.id);
      return clinicApi.updateClinicOnboarding(clinic.id, {
        ...request,
        // Vendor selection + codes are owned by the Capabilities section's
        // Electronic Chain of Custody row (SLV2-2999): carry, never edit, here.
        electronicChainOfCustody: fresh.electronicChainOfCustody ?? undefined,
        eScreenCode: fresh.eScreenCode ?? undefined,
        formFoxCode: fresh.formFoxCode ?? undefined,
        questCode: fresh.questCode ?? undefined,
      });
    },
    onSuccess: () => {
      setIsEditing(false);
      setShowSuccess(true);
      setValidationError(null);
      queryClient.invalidateQueries({ queryKey: ['clinic', clinic.id] });
      queryClient.invalidateQueries({ queryKey: ['clinics'] });
      onUpdate();
      setTimeout(() => setShowSuccess(false), 3000);
    },
  });

  const handleSave = () => {
    if (form.dotCertification && form.numberOfCertifiedDOTProfessionals < 1) {
      setValidationError('Number of certified DOT professionals is required when DOT certification is enabled');
      return;
    }
    // Mirrors the API's [Range(0, 999)] so the user sees this here instead of a 400.
    if (form.dotCertification && form.numberOfCertifiedDOTProfessionals > 999) {
      setValidationError('Number of certified DOT professionals must be between 1 and 999');
      return;
    }

    setValidationError(null);

    updateMutation.mutate({
      preferredMethodOfAppointment: form.preferredMethodOfAppointment || undefined,
      reportingMethod: form.reportingMethod || undefined,
      dotCertification: form.dotCertification,
      numberOfCertifiedDOTProfessionals: form.dotCertification ? form.numberOfCertifiedDOTProfessionals : undefined,
      w9OnFile: form.w9OnFile,
    });
  };

  const handleCancel = () => {
    setIsEditing(false);
    setForm(buildFormState(clinic));
    setValidationError(null);
    updateMutation.reset();
  };

  const handleEdit = () => {
    setForm(buildFormState(clinic));
    setValidationError(null);
    updateMutation.reset();
    setIsEditing(true);
  };

  const updateField = (field: keyof EditFormState, value: string | boolean | number) => {
    setForm(prev => {
      const updated = { ...prev, [field]: value };
      // Clear the DOT professional count when certification is unchecked
      if (field === 'dotCertification' && !value) {
        updated.numberOfCertifiedDOTProfessionals = 0;
      }
      return updated;
    });
  };

  const formatValue = (value: boolean | string | undefined | null, type: 'boolean' | 'string') => {
    if (type === 'boolean') {
      return value ? '✓ Yes' : '✗ No';
    }
    if (!value || value === '') {
      return 'Not Set';
    }
    return value as string;
  };

  return (
    <div className="info-section">
      {/* Section header with edit/save/cancel */}
      <div className="info-section-header">
        <button
          type="button"
          className="info-section-toggle"
          onClick={() => !isEditing && setIsCollapsed(c => !c)}
          aria-expanded={!isCollapsed}
          disabled={isEditing}
        >
          <ChevronDown size={16} className={`info-section-chevron ${isCollapsed ? 'collapsed' : ''}`} />
          <h3 className="info-section-title">Onboarding Information</h3>
        </button>
        <div className="info-section-actions">
          {showSuccess && (
            <span className="info-success-toast">
              <CheckCircle size={14} />
              Saved
            </span>
          )}
          {isEditing ? (
            <>
              <button
                type="button"
                className="svc-header-btn svc-header-btn-cancel"
                onClick={handleCancel}
                disabled={updateMutation.isPending}
              >
                <X size={14} />
                Cancel
              </button>
              <button
                type="button"
                className="svc-header-btn svc-header-btn-save"
                onClick={handleSave}
                disabled={updateMutation.isPending}
              >
                <Save size={14} />
                {updateMutation.isPending ? 'Saving...' : 'Save'}
              </button>
            </>
          ) : (
            <RequirePermission permission="UserCanEditClinicProfile">
              <button
                type="button"
                className="svc-edit-btn"
                onClick={handleEdit}
                title="Edit onboarding information"
              >
                <Pencil size={14} />
                Edit
              </button>
            </RequirePermission>
          )}
        </div>
      </div>

      {!isCollapsed && (
        <>
          {(updateMutation.isError || validationError) && (
            <div className="info-save-error">
              <AlertCircle size={14} />
              {validationError || getApiErrorMessage(updateMutation.error, 'Failed to save changes')}
            </div>
          )}

          <div className="info-content">
            <div className="info-details">
          {/* Row 1: Preferred Method + Reporting Method */}
          <div className="info-row">
            <div className="info-field">
              <label htmlFor="clinic-preferred-method">Preferred Appointment Method</label>
              {isEditing ? (
                <select
                  id="clinic-preferred-method"
                  className="info-select"
                  value={form.preferredMethodOfAppointment}
                  onChange={e => updateField('preferredMethodOfAppointment', e.target.value)}
                >
                  <option value="">Not Set</option>
                  <option value={PreferredMethodOfAppointment.Call}>Call</option>
                  <option value={PreferredMethodOfAppointment.Email}>Email</option>
                  <option value={PreferredMethodOfAppointment.WalkIn}>Walk-in</option>
                </select>
              ) : (
                <span>{formatValue(clinic.preferredMethodOfAppointment, 'string')}</span>
              )}
            </div>
            <div className="info-field">
              <label htmlFor="clinic-reporting-method">Reporting Method</label>
              {isEditing ? (
                <select
                  id="clinic-reporting-method"
                  className="info-select"
                  value={form.reportingMethod}
                  onChange={e => updateField('reportingMethod', e.target.value)}
                >
                  <option value="">Not Set</option>
                  <option value={ReportingMethod.Email}>Email</option>
                  <option value={ReportingMethod.Fax}>Fax</option>
                  <option value={ReportingMethod.Portal}>Portal</option>
                </select>
              ) : (
                <span>{formatValue(clinic.reportingMethod, 'string')}</span>
              )}
            </div>
          </div>

          {/* Row 3: DOT Certification + professional count */}
          <div className="info-row">
            <div className="info-field">
              <span className="info-label">DOT Certification</span>
              {isEditing ? (
                <label className="info-checkbox-label">
                  <input
                    type="checkbox"
                    checked={form.dotCertification}
                    onChange={e => updateField('dotCertification', e.target.checked)}
                  />
                  <span>Clinic is DOT certified</span>
                </label>
              ) : (
                <span>
                  {clinic.dotCertification
                    ? `✓ Yes (${clinic.numberOfCertifiedDOTProfessionals ?? 0} professionals)`
                    : '✗ No'}
                </span>
              )}
            </div>
            <div className="info-field">
              {isEditing && form.dotCertification ? (
                <>
                  <label htmlFor="clinic-dot-professionals">Certified DOT Professionals</label>
                  <input
                    id="clinic-dot-professionals"
                    type="number"
                    min={1}
                    max={999}
                    className="info-input"
                    value={form.numberOfCertifiedDOTProfessionals || ''}
                    onChange={e => updateField('numberOfCertifiedDOTProfessionals', Math.max(0, Number.parseInt(e.target.value, 10) || 0))}
                  />
                </>
              ) : null}
            </div>
          </div>

          {/* Row 4: W-9 on File. Translation Services moved to the Capabilities
              card as a capability (Clinic Network decision 02, SLV2-3301). */}
          <div className="info-row">
            <div className="info-field">
              <span className="info-label">W-9 on File</span>
              {isEditing ? (
                <label className="info-checkbox-label">
                  <input
                    type="checkbox"
                    checked={form.w9OnFile}
                    onChange={e => updateField('w9OnFile', e.target.checked)}
                  />
                  <span>W-9 form is on file</span>
                </label>
              ) : (
                <span>{formatValue(clinic.w9OnFile, 'boolean')}</span>
              )}
            </div>
          </div>
          </div>
        </div>
        </>
      )}
    </div>
  );
}
